/**
 * Analytics event taxonomy — RW-22.
 *
 * Single source of truth for all analytics event names. Import EVENTS from
 * here when calling `track()` so we never mistype an event name and can
 * grep / refactor in one place.
 *
 * Each tracked event is automatically enriched with these base properties
 * by the tracker (see ./track.js):
 *   - userId       Firebase UID
 *   - userEmail    agent email
 *   - userRole     agent / admin / tc / trainer / super_admin
 *   - office       agent's assigned office
 *   - sessionId    unique per browser session (sessionStorage)
 *   - timestamp    server timestamp on the Firestore mirror
 *
 * Per-call properties (e.g. listingId, postType, moduleName) are passed as
 * the second argument to `track()` and merged on top of the base properties.
 */

export const EVENTS = {
  // Auth
  LOGIN_SUCCESS: 'login_success',
  LOGIN_FAILED: 'login_failed',
  LOGOUT: 'logout',

  // Onboarding
  ONBOARDING_STARTED: 'onboarding_started',
  ONBOARDING_STEP_COMPLETED: 'onboarding_step_completed',
  ONBOARDING_COMPLETED: 'onboarding_completed',

  // Dashboard
  DASHBOARD_VIEWED: 'dashboard_viewed',

  // Feed / Posts
  POST_CREATED: 'post_created',
  POST_VIEWED: 'post_viewed',
  POST_REACTED: 'post_reacted',
  POST_COMMENTED: 'post_commented',
  POST_ACKNOWLEDGED: 'post_acknowledged',

  /* Coming Soon / Listings.
     RW-2313 instrumented these, but NOT every door — filed separately, do not
     read the emitters as complete coverage:
       · LISTING_STATUS_CHANGED: CLOSED by RW-2327 — all EIGHT feed_status doors
         emit (the ticket said five; AdminDashboard's bulk delete and per-row
         Delete, and PushToComingSoon's Remove, were three more, all of them
         unpublishes). Every emitter goes through trackListingStatusChange() in
         ./listingStatus.js, which owns the payload — `source` per door, `to:
         null` + `removed: true` for an unpublish, and `adminAction` for the
         admin-console doors. Renew is exempt ON PURPOSE (from === to).
         src/analyticsListingStatusGuards.test.js fails if a ninth door appears.
       · BUYER_NEED_CREATED / LISTING_CREATED: FIXED by RW-2326, and no longer
         emitted at call sites. Both now fire from inside their service
         (buyersService.create / listingsService.createListing) for every door,
         with the door passed in as `{ door }` and written to the `source`
         property. The exceptions are the doors whose records are born inside
         `dealsApi createDeal` and never touch those services — DealsNewDeal
         and DealsChatStart for needs, ListingFormModal's ListingCreateModal
         for listings — which keep their own call-site emits, guarded on having
         SENT the sub-payload (`res.buyerId` also comes back when linkBuyerId
         merely LINKED an existing need). The inventory is 5 need doors and 3
         listing doors; both counts were short in the ticket AND in the first
         cut of the fix. src/analyticsCreateDoorGuards.test.js pins the split in
         both directions — widen its by-name list when you add a door, never
         delete it. Do not re-add a service call-site emit: it double-counts. */
  LISTING_CREATED: 'listing_created',
  LISTING_VIEWED: 'listing_viewed',
  LISTING_STATUS_CHANGED: 'listing_status_changed',
  SHOWING_REQUESTED: 'showing_requested',

  // Buyer Needs
  BUYER_NEED_CREATED: 'buyer_need_created',
  BUYER_NEED_VIEWED: 'buyer_need_viewed',
  BUYER_MATCH_VIEWED: 'buyer_match_viewed',

  // Transactions
  TRANSACTION_CREATED: 'transaction_created',
  TRANSACTION_VIEWED: 'transaction_viewed',
  TRANSACTION_STATUS_CHANGED: 'transaction_status_changed',
  CHECKLIST_SUBMITTED: 'checklist_submitted',

  // Tasks
  TASK_CREATED: 'task_created',
  TASK_COMPLETED: 'task_completed',

  // FLOW
  FLOW_SECTION_COMPLETED: 'flow_section_completed',
  FLOW_WEEK_SUBMITTED: 'flow_week_submitted',
  FLOW_SHARED_TO_FEED: 'flow_shared_to_feed',

  // Contacts
  CONTACT_CREATED: 'contact_created',
  CONTACT_VIEWED: 'contact_viewed',
  CONTACT_NOTE_ADDED: 'contact_note_added',

  // Messages
  MESSAGE_SENT: 'message_sent',

  // Events
  EVENT_RSVP: 'event_rsvp',

  /* Navigation.

     MODULE_ENTERED counts SCREEN MOUNTS — not visits, not sessions, never
     people (RW-2328 wrote this down; the behaviour of every board is
     unchanged). userId rides every event (see the base properties above), so
     counting people means counting distinct userId, never counting rows.

     ── What actually produces one ──────────────────────────────────────────
     · A mount of the module's screen. App.js's route ladder swaps COMPONENT
       TYPES for a record route, so opening a card UNMOUNTS the board and Back
       REMOUNTS it: browsing ten Coming Soon cards and backing out of each is
       ELEVEN `coming_soon` events, not one. Buyers does the same (/buyers/:id
       swaps BuyersPage for BuyerDetail).
     · RW-2328: the two Market record screens (ComingSoonDetail,
       BuyerNeedDetail) emit ONE event when the arrival did not come from their
       own board — a notification or email deep link (RW-2239), a pasted URL,
       Search, another module's rail. That is how a notification-driven agent
       reaches the module. Gated on router state `fromModule`, which only the
       module's own board sets, so browsing INSIDE the module adds nothing: a
       board → card → Back round trip is two events, never three.
     · Some modules' record routes render the MODULE ITSELF, so a cold deep
       link to one already counted, long before RW-2328: /posts/:id resolves to
       nav 'Home' and mounts <Feed> (feed), and the Events and Ninja FLOW
       sub-routes mount their own module shells. A deal record (/deals/:id)
       does NOT — it mounts DealDetail, which emits nothing.

     ⚖ So the doors differ per module. Compare a module against ITSELF over
     time; do not rank modules against each other on this number.

     ── 🔴 FOUR ROWS WERE WRONG BEFORE RW-2353 — do not read the history ────
     Fixed in RW-2353. Listed because the rows are fixed GOING FORWARD only:
     every event written before that deploy still carries the old meaning, so a
     chart that spans the cutover is comparing two different measurements.
     · `contacts` and `messages` read ZERO FOREVER. Their only emitters were
       ContactsPage.js and Messages.js, neither imported by anything in src/ but
       its own test — App.js renders ContactsApp and chat/ChatDrawer, which
       emitted nothing. `messages` hid especially well: App.js:335 binds the
       NAME `Messages` to './components/chat/ChatDrawer', so grepping App.js for
       `<Messages` said the module was live. A zero there means "nothing writes
       it", not "nobody uses it" — read those two rows as NO DATA, not as low
       adoption. The emitters now live in contacts/ContactsApp.js and
       chat/ChatDrawer.js.
     · `recognition` did not mean what its name says. Its only emitter was
       RecognitionWall, imported solely by PeopleDirectory, so it fired when
       someone opened the People › Recognition TAB; entering the Recognition
       module emitted nothing. That row is not zero and not adoption either —
       it is a count of People-tab opens. The emitter now lives in
       recognition/RecognitionModule.js and RecognitionWall carries none: two
       emitters for one slug would fold two questions into one unreadable row.
       If the People tab ever needs its own number, it needs its own SLUG.
     · `flow` DOUBLE-FIRED on the consolidated view: NinjaFlow/index.js emitted,
       then mounted Consolidated.js → <FlowScorecard embedded />, which emitted
       again. Historical `flow` is inflated by one event per visit to that one
       screen — not halved, since the other FLOW screens fired once. The
       embedded card no longer emits; the module shell owns the event.
     · EventsPage.js was a second, unreachable `events` emitter; the live one was
       always events/EventsApp.js, so that row was never wrong. Removed anyway —
       a file nothing renders must not satisfy a guard.
     src/analyticsGuards.test.js pins every emitter against MODULES below, and
     since RW-2353 its direction 5 walks the real import graph from App.js, so
     an emitter in a file nothing renders now FAILS instead of passing. It still
     cannot prove an emitter RUNS — that is the runtime suites
     (moduleEntered.liveSurfaces.test.js and the *.analytics files).

     ── Nobody reads this yet ───────────────────────────────────────────────
     Nothing reads properties.moduleName. The adoption dashboards are fed by
     src/utils/analyticsLogger.js's ROUTE_TO_FEATURE map, a different pipeline.
     These events are write-only until a reader exists. */
  MODULE_ENTERED: 'module_entered',
  // RW-1789: RW-1782 re-cut instrumentation. Section/item selections on the
  // rail, plus every route arrival tagged with HOW it was reached (via:
  // rail | search | other). The via split is the number that matters — a
  // destination reached mostly by search is filed in the wrong place.
  NAV_SECTION_SELECTED: 'nav_section_selected',
  NAV_ITEM_SELECTED: 'nav_item_selected',
  NAV_ROUTE_REACHED: 'nav_route_reached',

  // Documents
  DOCUMENT_UPLOADED: 'document_uploaded',
  DOCUMENT_VIEWED: 'document_viewed',

  // Recognition
  RECOGNITION_GIVEN: 'recognition_given',

  // What's New changelog (RW-1642). seen/tried receipts also land in Postgres
  // (app.changelog_receipts, distinct-user counts for the Ship Log) — these GA
  // twins keep the funnel visible alongside the rest of the module metrics.
  WHATS_NEW_VIEWED: 'whats_new_viewed',
  WHATS_NEW_TRIED: 'whats_new_tried',
};

/**
 * Canonical module names for MODULE_ENTERED events. Using constants prevents
 * a typo from splitting one module's metric across two slugs.
 *
 * RW-2313: this list and the MODULE_ENTERED call sites are a PAIRED REGISTRY —
 * a key defined here is only ever REACHED through an emitter somewhere else, so
 * a key with no emitter is a metric that reads zero forever and looks like
 * "nobody uses it" rather than "nothing writes it". `src/analyticsGuards.test.js`
 * pins the pair in both directions: every key below has at least one emitter,
 * and every emitter names a key that exists here.
 */
export const MODULES = {
  FEED: 'feed',
  COMING_SOON: 'coming_soon',
  BUYERS: 'buyers',
  // The Deals-side saved-search board (/connect/buyer-needs), NOT the Buyers CRM
  // page (/buyers) above it — separate rail rows, separate adoption questions.
  BUYER_NEEDS: 'buyer_needs',
  TRANSACTIONS: 'transactions',
  CONTACTS: 'contacts',
  MESSAGES: 'messages',
  EVENTS: 'events',
  FLOW: 'flow',
  LEADERBOARD: 'leaderboard',
  DOCUMENTS: 'documents',
  RECOGNITION: 'recognition',
};
